import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  getGroupStats,
  getDesignerGroupMapping,
  listGroupConversations,
  listGroupMessages,
  setGroupConversationFavorite,
  setGroupConversationMode,
  saveDesignerGroupMapping,
} from '../../api/adminApi.js';
import type {
  GroupConversation,
  DesignerGroupMapping,
  GroupMessage,
  GroupStats,
  ListConversationsPagination,
} from '../../api/types.js';
import '../../styles/groups.css';
import { readGroupId, writeGroupId } from './group-navigation.js';

const GROUP_PAGE_SIZE = 50;
const MESSAGE_PAGE_SIZE = 100;
type GroupListScope = 'all' | 'favorite' | 'manual';

export function GroupConversationConsole({ onToast, onOpenAnalysis }: {
  onToast(message: string, type?: 'success' | 'error'): void;
  onOpenAnalysis(id: number): void;
}) {
  const [conversations, setConversations] = useState<GroupConversation[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const selectedId = useRef<number | null>(null);
  const initialId = useRef(readGroupId(window.location.search));
  const [selectedConversation, setSelectedConversation] = useState<GroupConversation | null>(null);
  const messageRequest = useRef(0);
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [stats, setStats] = useState<GroupStats | null>(null);
  // 保留旧搜索链接；精确群链接独立于名单、搜索与分页加载。
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get('q') ?? '');
  const [scope, setScope] = useState<GroupListScope>('all');
  const [listOffset, setListOffset] = useState(0);
  const [listPagination, setListPagination] = useState<ListConversationsPagination>(emptyPagination(GROUP_PAGE_SIZE));
  const [messagePagination, setMessagePagination] = useState<ListConversationsPagination>(emptyPagination(MESSAGE_PAGE_SIZE));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedDates, setAppliedDates] = useState({ startDate: '', endDate: '' });
  const [listLoading, setListLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [modeChanging, setModeChanging] = useState(false);
  const [designerMapping, setDesignerMapping] = useState<DesignerGroupMapping | null>(null);
  const [mappingTargetClusterId, setMappingTargetClusterId] = useState('');
  const [mappingTargetWxClusterId, setMappingTargetWxClusterId] = useState('');
  const [mappingTargetName, setMappingTargetName] = useState('');
  const [mappingEnabled, setMappingEnabled] = useState(true);
  const [mappingSaving, setMappingSaving] = useState(false);

  const loadList = useCallback(async (search: string, offset: number) => {
    setListLoading(true);
    try {
      const [list, summary] = await Promise.all([
        listGroupConversations({
          q: search || undefined,
          limit: GROUP_PAGE_SIZE,
          offset,
          days: scope === 'manual' ? undefined : 30,
          favoriteOnly: scope === 'favorite',
          mode: scope === 'manual' ? 'manual' : undefined,
        }),
        getGroupStats(),
      ]);
      setConversations(list.conversations);
      setListPagination(list.pagination ?? emptyPagination(GROUP_PAGE_SIZE));
      setStats(summary.stats);
    } catch (error) {
      onToast(error instanceof Error ? error.message : '群聊加载失败', 'error');
    } finally {
      setListLoading(false);
    }
  }, [onToast, scope]);

  const loadMessagePage = useCallback(async (
    id: number,
    offset: number,
    dates: { startDate: string; endDate: string },
  ) => {
    const request = ++messageRequest.current;
    setMessageLoading(true);
    try {
      const response = await listGroupMessages(id, {
        limit: MESSAGE_PAGE_SIZE,
        offset,
        startDate: dates.startDate || undefined,
        endDate: dates.endDate || undefined,
      });
      if (selectedId.current !== id || request !== messageRequest.current) return;
      setSelectedConversation(response.conversation);
      setMessages(response.messages);
      setMessagePagination(response.pagination ?? emptyPagination(MESSAGE_PAGE_SIZE));
    } catch (error) {
      if (selectedId.current !== id || request !== messageRequest.current) return;
      onToast(error instanceof Error ? error.message : '群消息加载失败', 'error');
    } finally {
      if (selectedId.current === id && request === messageRequest.current) setMessageLoading(false);
    }
  }, [onToast]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const search = query.trim();
      setListOffset(0);
      void loadList(search, 0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [loadList, query]);

  const selectConversation = useCallback((id: number) => {
    selectedId.current = id;
    setActiveId(id);
    writeGroupId(id);
    setSelectedConversation(null);
    setMessages([]);
    setDesignerMapping(null);
    setMappingTargetClusterId('');
    setMappingTargetWxClusterId('');
    setMappingTargetName('');
    setMappingEnabled(true);
    const dates = { startDate, endDate };
    setAppliedDates(dates);
    void loadMessagePage(id, 0, dates);
    void getDesignerGroupMapping(id).then(({ mapping }) => {
      if (selectedId.current !== id) return;
      setDesignerMapping(mapping);
      setMappingTargetClusterId(mapping ? String(mapping.targetClusterId) : '');
      setMappingTargetWxClusterId(mapping?.targetWxClusterId ?? '');
      setMappingTargetName(mapping?.targetClusterName ?? '');
      setMappingEnabled(mapping?.enabled ?? true);
    }).catch((error) => {
      if (selectedId.current !== id) return;
      setDesignerMapping(null);
      onToast(error instanceof Error ? error.message : '设计师群映射加载失败', 'error');
    });
  }, [endDate, loadMessagePage, onToast, startDate]);

  useEffect(() => {
    const id = initialId.current;
    initialId.current = null;
    if (id !== null) selectConversation(id);
  }, [selectConversation]);

  const applyDateFilter = useCallback((event: FormEvent) => {
    event.preventDefault();
    if (!activeId) return;
    const dates = { startDate, endDate };
    setAppliedDates(dates);
    void loadMessagePage(activeId, 0, dates);
  }, [activeId, endDate, loadMessagePage, startDate]);

  const clearDateFilter = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setAppliedDates({ startDate: '', endDate: '' });
    if (activeId) void loadMessagePage(activeId, 0, { startDate: '', endDate: '' });
  }, [activeId, loadMessagePage]);

  const active = useMemo(
    () => selectedConversation?.id === activeId ? selectedConversation : conversations.find((item) => item.id === activeId) ?? null,
    [activeId, conversations, selectedConversation],
  );


  const toggleMode = useCallback(async () => {
    if (!active) return;
    const nextMode = active.mode === 'manual' ? 'auto' : 'manual';
    setModeChanging(true);
    try {
      const response = await setGroupConversationMode(active.id, nextMode);
      const leavesCurrentScope = scope === 'manual' && nextMode === 'auto';
      setConversations((current) => leavesCurrentScope
        ? current.filter((item) => item.id !== response.conversation.id)
        : current.map((item) => (
            item.id === response.conversation.id ? { ...item, ...response.conversation } : item
          )));
      if (leavesCurrentScope) {
        selectedId.current = null;
        setActiveId(null);
        writeGroupId(null);
        setMessages([]);
        setMessagePagination(emptyPagination(MESSAGE_PAGE_SIZE));
      } else {
        await loadMessagePage(active.id, 0, appliedDates);
      }
      onToast(nextMode === 'manual' ? '已暂停该群 AI 回复' : '已恢复该群 AI 回复', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '群接管状态切换失败', 'error');
    } finally {
      setModeChanging(false);
    }
  }, [active, appliedDates, loadMessagePage, onToast, scope]);

  const toggleFavorite = useCallback(async (conversation: GroupConversation) => {
    try {
      const { conversation: updated } = await setGroupConversationFavorite(
        conversation.id,
        !conversation.favorite,
      );
      setConversations((current) => {
        if (scope === 'favorite' && !updated.favorite) return current.filter((item) => item.id !== updated.id);
        return current.map((item) => item.id === updated.id ? { ...item, ...updated } : item);
      });
      if (scope === 'favorite' && !updated.favorite && activeId === updated.id) {
        selectedId.current = null;
        setActiveId(null);
        writeGroupId(null);
        setMessages([]);
        setMessagePagination(emptyPagination(MESSAGE_PAGE_SIZE));
      }
      onToast(updated.favorite ? '已收藏群聊' : '已取消收藏', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '收藏状态更新失败', 'error');
    }
  }, [activeId, onToast, scope]);

  const saveMapping = useCallback(async (event: FormEvent) => {
    event.preventDefault();
    if (!active) return;
    const targetClusterId = Number(mappingTargetClusterId);
    if (!Number.isInteger(targetClusterId) || targetClusterId <= 0) {
      onToast('请填写有效的设计师群 cluster_id', 'error');
      return;
    }
    setMappingSaving(true);
    try {
      const { mapping } = await saveDesignerGroupMapping(active.id, {
        targetClusterId,
        targetWxClusterId: mappingTargetWxClusterId.trim(),
        targetClusterName: mappingTargetName.trim() || null,
        enabled: mappingEnabled,
      });
      setDesignerMapping(mapping);
      onToast('设计师群映射已保存', 'success');
    } catch (error) {
      onToast(error instanceof Error ? error.message : '设计师群映射保存失败', 'error');
    } finally {
      setMappingSaving(false);
    }
  }, [active, mappingEnabled, mappingTargetClusterId, mappingTargetName, mappingTargetWxClusterId, onToast]);

  return (
    <main className="group-console">
      <header className="group-console-header">
        <div>
          <h1>群聊</h1>
          <p>{scope === 'favorite' ? '已收藏群聊' : scope === 'manual' ? '已转人工群聊' : '全部历史'}</p>
        </div>
        <button
          type="button"
          onClick={() => void loadList(query.trim(), listOffset)}
          disabled={listLoading}
        >
          刷新
        </button>
      </header>
      <section className="group-stats" aria-label="群聊统计">
        <Stat label="群数" value={stats?.conversationCount} />
        <Stat label="消息" value={stats?.messageCount} />
        <Stat label="@触发" value={stats?.triggerCount} />
        <Stat label="回复" value={stats?.outboundCount} />
        <Stat label="平均延迟" value={formatLatency(stats?.averageAiLatencyMs)} />
        <Stat label="P95 延迟" value={formatLatency(stats?.p95AiLatencyMs)} />
      </section>
      <div className="group-workspace">
        <aside className="group-list-pane">
          <div className="group-list-scope segmented" role="group" aria-label="群聊范围">
            <button
              type="button"
              className={`segment${scope === 'all' ? ' active' : ''}`}
              aria-pressed={scope === 'all'}
              onClick={() => setScope('all')}
            >
              全部
            </button>
            <button
              type="button"
              className={`segment${scope === 'favorite' ? ' active' : ''}`}
              aria-pressed={scope === 'favorite'}
              onClick={() => setScope('favorite')}
            >
              已收藏
            </button>
            <button
              type="button"
              className={`segment${scope === 'manual' ? ' active' : ''}`}
              aria-pressed={scope === 'manual'}
              onClick={() => setScope('manual')}
            >
              已转人工
            </button>
          </div>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜索群名、消息或成员 ID"
            aria-label="搜索群聊"
          />
          <div className="group-list">
            {conversations.map((item) => (
              <div key={item.id} className={`group-list-item${item.id === activeId ? ' active' : ''}`}>
                <button
                  type="button"
                  className="group-list-select"
                  onClick={() => selectConversation(item.id)}
                >
                  <strong>
                    <span className="group-list-name">{item.clusterName || `群 ${item.clusterId}`}</span>
                    {item.mode === 'manual' ? <span className="group-manual-badge">已转人工</span> : null}
                  </strong>
                  <span>{previewContent(item.lastMessage)}</span>
                  <small>{formatTime(item.lastMessageAt)} · {item.messageCount} 条</small>
                </button>
                <button
                  type="button"
                  className={`group-favorite${item.favorite ? ' active' : ''}`}
                  aria-label={item.favorite ? '取消收藏' : '收藏群聊'}
                  aria-pressed={item.favorite}
                  title={item.favorite ? '取消收藏' : '收藏群聊'}
                  onClick={() => void toggleFavorite(item)}
                >
                  {item.favorite ? '★' : '☆'}
                </button>
              </div>
            ))}
            {!listLoading && conversations.length === 0 ? (
              <div className="group-empty">{scope === 'manual' ? '暂无已转人工群聊' : '暂无群聊'}</div>
            ) : null}
          </div>
          <Pagination
            pagination={listPagination}
            loading={listLoading}
            previousLabel="上一页"
            nextLabel="下一页"
            onPrevious={() => {
              const offset = listPagination.previousOffset ?? 0;
              setListOffset(offset);
              void loadList(query.trim(), offset);
            }}
            onNext={() => {
              if (listPagination.nextOffset === null) return;
              setListOffset(listPagination.nextOffset);
              void loadList(query.trim(), listPagination.nextOffset);
            }}
          />
        </aside>
        <section className="group-message-pane">
          <header>
            <div>
              <h2>{active?.clusterName || (active ? `群 ${active.clusterId}` : '选择群聊')}</h2>
              {active ? <p>工作号 {active.botWechatRecordId} · cluster {active.clusterId}</p> : null}
            </div>
            {active ? (
              <div className="group-mode-controls">
                <button type="button" className="group-analysis-link" onClick={() => onOpenAnalysis(active.id)}>智能分析</button>
                <span className={active.mode === 'manual' ? 'manual' : ''}>
                  {active.mode === 'manual' ? '人工接管' : 'AI 回复'}
                </span>
                <button type="button" onClick={() => void toggleMode()} disabled={modeChanging}>
                  {active.mode === 'manual' ? '切回 AI' : '接管'}
                </button>
              </div>
            ) : null}
          </header>
          <form className="group-message-filters" onSubmit={applyDateFilter}>
            <label>
              <span>开始日期</span>
              <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            </label>
            <label>
              <span>结束日期</span>
              <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
            </label>
            <button className="button" type="submit" disabled={!active || messageLoading}>查询</button>
            <button className="button ghost" type="button" onClick={clearDateFilter} disabled={!active || messageLoading}>清除</button>
          </form>
          <form className="group-mapping-panel" onSubmit={(event) => void saveMapping(event)}>
            <strong>需求转发目标群</strong>
            <label className="group-mapping-field">
              <span>群编号</span>
              <input
                inputMode="numeric"
                value={mappingTargetClusterId}
                onChange={(event) => setMappingTargetClusterId(event.target.value)}
                placeholder="例如 2051449"
                title="Apollo 的 cluster_id"
                disabled={!active}
              />
            </label>
            <label className="group-mapping-field">
              <span>微信标识</span>
              <input
                value={mappingTargetWxClusterId}
                onChange={(event) => setMappingTargetWxClusterId(event.target.value)}
                placeholder="例如 46332342442@chatroom"
                title="Apollo 的 wx_cluster_id"
                disabled={!active}
              />
            </label>
            <label className="group-mapping-field">
              <span>群名称</span>
              <input
                value={mappingTargetName}
                onChange={(event) => setMappingTargetName(event.target.value)}
                placeholder="例如 七兔设计师对接群"
                disabled={!active}
              />
            </label>
            <label>
              <input
                type="checkbox"
                checked={mappingEnabled}
                onChange={(event) => setMappingEnabled(event.target.checked)}
                disabled={!active}
              />
              启用
            </label>
            <button className="button" type="submit" disabled={!active || mappingSaving}>
              {mappingSaving ? '保存中' : designerMapping ? '更新' : '保存'}
            </button>
          </form>
          <div className="group-messages">
            {messages.map((message) => (
              <article key={message.id} id={`group-message-${message.id}`} className={`group-message ${message.direction}`}>
                <div className="group-message-meta">
                  <strong title={message.direction === 'in' ? message.senderWechatId : undefined}>
                    {message.direction === 'out' ? '工作号' : message.senderNickname || message.senderWechatId}
                  </strong>
                  {message.triggered ? <span className="group-trigger-mark">@触发</span> : null}
                  <time>{formatTime(message.createdAt)}</time>
                </div>
                {message.content ? <p>{message.content}</p> : null}
                {message.mediaType === 'image' && message.mediaUrl ? (
                  <a href={message.mediaUrl} target="_blank" rel="noreferrer" className="group-message-image-link">
                    <img src={message.mediaUrl} alt={message.content || '群聊图片'} className="group-message-image" />
                  </a>
                ) : message.mediaUrl ? (
                  <a href={message.mediaUrl} target="_blank" rel="noreferrer" className="group-message-attachment">
                    查看{mediaLabel(message.mediaType)}
                  </a>
                ) : !message.content ? <p>[非文本消息]</p> : null}
                {message.aiLatencyMs ? <small>响应 {(message.aiLatencyMs / 1000).toFixed(1)} 秒</small> : null}
              </article>
            ))}
            {messageLoading ? <div className="group-empty">加载中...</div> : null}
            {active && !messageLoading && messages.length === 0 ? <div className="group-empty">该时间范围暂无消息</div> : null}
          </div>
          <Pagination
            pagination={messagePagination}
            loading={messageLoading}
            previousLabel="较新消息"
            nextLabel="更早消息"
            onPrevious={() => {
              if (!activeId) return;
              void loadMessagePage(activeId, messagePagination.previousOffset ?? 0, appliedDates);
            }}
            onNext={() => {
              if (!activeId || messagePagination.nextOffset === null) return;
              void loadMessagePage(activeId, messagePagination.nextOffset, appliedDates);
            }}
          />
        </section>
      </div>
    </main>
  );
}

function Pagination({
  pagination,
  loading,
  previousLabel,
  nextLabel,
  onPrevious,
  onNext,
}: {
  pagination: ListConversationsPagination;
  loading: boolean;
  previousLabel: string;
  nextLabel: string;
  onPrevious(): void;
  onNext(): void;
}) {
  const start = pagination.count === 0 ? 0 : pagination.offset + 1;
  const end = pagination.offset + pagination.count;
  return (
    <footer className="group-pagination">
      <button className="button ghost" type="button" disabled={!pagination.hasPrevious || loading} onClick={onPrevious}>
        {previousLabel}
      </button>
      <span>{loading ? '加载中...' : `${start}-${end}`}</span>
      <button className="button ghost" type="button" disabled={!pagination.hasNext || loading} onClick={onNext}>
        {nextLabel}
      </button>
    </footer>
  );
}

function Stat({ label, value }: { label: string; value: string | number | null | undefined }) {
  return <div><span>{label}</span><strong>{value ?? '-'}</strong></div>;
}

function formatLatency(value: number | null | undefined): string {
  return value == null ? '-' : `${(value / 1000).toFixed(1)}s`;
}

function formatTime(value: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function previewContent(value: string | null | undefined): string {
  if (!value) return '暂无消息';
  const normalized = value.trim();
  if (normalized.startsWith('{') && normalized.includes('"url"')) return '[图片或附件]';
  return normalized;
}

function mediaLabel(mediaType: string | null): string {
  if (mediaType === 'video') return '视频';
  if (mediaType === 'voice') return '语音';
  return '附件';
}

function emptyPagination(limit: number): ListConversationsPagination {
  return {
    limit,
    offset: 0,
    count: 0,
    hasPrevious: false,
    hasNext: false,
    nextOffset: null,
    previousOffset: null,
  };
}
